
import {  useState, useEffect, useMemo, useCallback } from 'react';
import { Search, X, UserCheck,  Check,  CheckCircle2,  AlertTriangle,  Loader2, Bell
} from 'lucide-react';
const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/+$/, '')}/api`;

// ============================================================================
// ROLE-BASED NOTIFICATION ENGINE (DB-DRIVEN)
// ============================================================================

const NOTIFICATION_ROLES = [
  { id: 'whole_team', label: 'General', color: 'bg-slate-100 text-slate-800 border-slate-300' },
  { id: 'sales', label: 'Sales', color: 'bg-purple-50 text-purple-800 border-purple-200' },
  { id: 'planning', label: 'Planning', color: 'bg-pink-50 text-pink-800 border-pink-200' },
  { id: 'purchasing', label: 'Purchasing', color: 'bg-rose-50 text-rose-800 border-rose-200' },
  { id: 'production', label: 'Production', color: 'bg-amber-50 text-amber-800 border-amber-200' },
  { id: 'operations', label: 'Operations', color: 'bg-blue-50 text-blue-800 border-blue-200' },
  { id: 'logistics', label: 'Logistics', color: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
  { id: 'shipping_dep', label: 'Shipping Dep', color: 'bg-cyan-50 text-cyan-800 border-cyan-200' },
  { id: 'accounting', label: 'Accounting', color: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  // { id: 'management', label: 'Management & Escalations', color: 'bg-lime-50 text-lime-800 border-lime-200' },
];

function resolveLoggedInUser(propUser = null) {
  if (propUser && (propUser.email || propUser.name || propUser._id || propUser.id)) {
    return {
      userId: String(propUser._id || propUser.id || propUser.userId || ''),
      name: String(
        propUser.name ||
          propUser.fullName ||
          propUser.username ||
          propUser.email ||
          ''
      ).trim(),
      email: String(propUser.email || '').toLowerCase().trim(),
    };
  }

  const storageKeys = ['user', 'currentUser', 'authUser', 'profile', 'userInfo'];
  for (const key of storageKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const u = parsed?.user || parsed?.data || parsed;
      if (u && (u.email || u.name || u.username || u._id || u.id)) {
        return {
          userId: String(u._id || u.id || u.userId || ''),
          name: String(
            u.name ||
              u.fullName ||
              u.username ||
              [u.firstName, u.lastName].filter(Boolean).join(' ') ||
              u.email ||
              ''
          ).trim(),
          email: String(u.email || '').toLowerCase().trim(),
        };
      }
    } catch {
      // Ignore parse errors
    }
  }

  const tokenKeys = ['token', 'authToken', 'accessToken', 'jwt'];
  for (const tKey of tokenKeys) {
    try {
      const token = localStorage.getItem(tKey);
      if (!token || !token.includes('.')) continue;
      const base64Payload = token.split('.')[1];
      const payload = JSON.parse(atob(base64Payload));
      if (payload && (payload.email || payload.name || payload.username || payload.id || payload._id)) {
        return {
          userId: String(payload._id || payload.id || payload.sub || ''),
          name: String(payload.name || payload.username || payload.email || '').trim(),
          email: String(payload.email || '').toLowerCase().trim(),
        };
      }
    } catch {
      // Ignore decode errors
    }
  }

  return null;
}

function buildRoleWorkflowNotifications(
  ordersList = [],
  manualGroups = {},
  stepStateMap = {},
  roleAssignments = {}
) {
  const generated = [];
  const sourceOrders = Array.isArray(ordersList) ? ordersList : [];

  const orderToGroupMap = {};
  if (manualGroups && typeof manualGroups === 'object') {
    Object.values(manualGroups).forEach((mg) => {
      if (mg && Array.isArray(mg.orderIds)) {
        mg.orderIds.forEach((oid) => {
          orderToGroupMap[oid] = mg.parentPO || mg.id;
        });
      }
    });
  }

  const getAssigneesLabel = (roleKey) => {
    if (roleKey === 'whole_team') return 'Whole Team';
    const list = roleAssignments[roleKey];
    if (!Array.isArray(list) || list.length === 0) return 'Unassigned';
    return list.map((u) => (typeof u === 'string' ? u : u.name || u.email)).join(', ');
  };

  sourceOrders.forEach((order) => {
    const rawDoc = order._raw || order;
    const orderId = String(
      order._id?.$oid ||
        order._id ||
        order.id ||
        order.salesOrderId ||
        order.salesOrderNumber ||
        order.soNumber ||
        ''
    );
    if (!orderId) return;

    const soNum =
      order.salesOrderNumber ||
      order.soNumber ||
      order.salesOrder ||
      order.number ||
      rawDoc.salesOrderNumber ||
      rawDoc.salesOrder ||
      rawDoc.number ||
      `SO-${orderId}`;

    const clientName =
      order.customer ||
      order.clientName ||
      rawDoc.customer ||
      'Unknown Client';

    const rawAmount =
      order.amountTaxIncl ??
      order.amount ??
      order.totalAmount ??
      rawDoc.amountTaxIncl ??
      rawDoc.amount ??
      0;

    const formattedValue = Number(rawAmount).toLocaleString('en-CA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const rawDeadline =
      order.deadline?.$date ||
      order.deadline ||
      order.shipDate ||
      order.plannedShipDate ||
      rawDoc.deadline?.$date ||
      rawDoc.deadline ||
      rawDoc.orderDateTime?.$date ||
      rawDoc.orderDateTime ||
      'TBD';

    const deadlineStr =
      typeof rawDeadline === 'string' && rawDeadline.includes('T')
        ? rawDeadline.split('T')[0]
        : String(rawDeadline);

    const poFamilyLabel =
      order.poFamily ||
      rawDoc.poFamily ||
      orderToGroupMap[order.id] ||
      orderToGroupMap[orderId] ||
      order.parentPO ||
      null;

    const targetDisplayLabel = poFamilyLabel
      ? `PO Family ${poFamilyLabel} (${soNum})`
      : soNum;

    const stateOverride = stepStateMap[orderId] || stepStateMap[soNum] || {};

    const isProductionPlanned =
      stateOverride.productionPlanned ??
      Boolean(
        order.productionPlanned ??
          rawDoc.productionPlanned ??
          (order.productionStatus === 'Done' || order.productionStatus === 'Completed')
      );

    const isMaterialsPurchased =
      stateOverride.materialsPurchased ??
      Boolean(
        order.materialsPurchased ??
          rawDoc.materialsPurchased ??
          (order.purchasingStatus === 'Done' || order.purchasingStatus === 'Completed')
      );

    const isPacked =
      stateOverride.isPacked ??
      Boolean(
        order.isPacked ||
          rawDoc.isPacked ||
          order.quantityStatus === 'Fulfilled' ||
          rawDoc.quantityStatus === 'Fulfilled' ||
          order.packedStatus === 'Packed' ||
          (order.packingLists && String(order.packingLists).trim().length > 0)
      );

    const isShipped =
      stateOverride.isShipped ??
      Boolean(
        order.isShipped ||
          rawDoc.isShipped ||
          order.shippingStatus === 'Shipped' ||
          rawDoc.shippingStatus === 'Shipped' ||
          order.shippedStatus === 'Shipped'
      );

    const isInvoiced =
      stateOverride.isInvoiced ??
      Boolean(
        order.isInvoiced ||
          rawDoc.isInvoiced ||
          order.status === 'Invoiced' ||
          rawDoc.status === 'Invoiced' ||
          (order.invoiceNumber && String(order.invoiceNumber).trim().length > 0) ||
          (rawDoc.invoiceNumber && String(rawDoc.invoiceNumber).trim().length > 0)
      );

    const isPackedShippedInvoiced = isPacked && isShipped && isInvoiced;

    const isCustomsRequired =
      stateOverride.customsRequired ??
      Boolean(
        order.customsRequired ||
          rawDoc.customsRequired ||
          order.customsDocsRequired ||
          Number(order.internationalTrade ?? rawDoc.internationalTrade ?? 0) > 0 ||
          (rawDoc.country && String(rawDoc.country).toUpperCase() !== 'CAN') ||
          (rawDoc.shippingAddress?.country &&
            String(rawDoc.shippingAddress.country).toUpperCase() !== 'CAN' &&
            String(rawDoc.shippingAddress.country).toUpperCase() !== 'CANADA')
      );

    const isCustomsPrepared =
      stateOverride.customsPrepared ??
      Boolean(
        order.customsPrepared ??
          rawDoc.customsPrepared ??
          order.customsStatus === 'Done'
      );

    const isAccountingSubmitted =
      stateOverride.accountingSubmitted ??
      Boolean(
        order.accountingSubmitted ??
          rawDoc.accountingSubmitted ??
          order.invoiceSubmittedToCustomer
      );

    const createdTimestamp =
      order.creationDate?.$date ||
      order.creationDate ||
      rawDoc.creationDate?.$date ||
      rawDoc.creationDate ||
      new Date().toISOString();

    // 0. Whole Team Broadcast
    
    generated.push({
      id: `wf_whole_team_${soNum}`,
      orderId,
      soNumber: soNum,
      role: 'whole_team',
      roleLabel: 'Whole Team',
      assignedTo: 'Whole Team',
      actionRequired: false,
      stepKey: 'broadcast_new_so',
      stepCompleted: true,
      title: `New SO: ${soNum}`,
      message: `New SO: ${soNum} | Client: ${clientName} | Value: $${formattedValue} | Deadline: ${deadlineStr}`,
      timestamp: createdTimestamp,
    });     

    // 1. Production Role
    generated.push({
      id: `wf_production_${soNum}`,
      orderId,
      soNumber: soNum,
      role: 'production',
      roleLabel: 'Production',
      assignedTo: getAssigneesLabel('production'),
      actionRequired: !isProductionPlanned,
      stepKey: 'productionPlanned',
      stepCompleted: isProductionPlanned,
      nextStepHint: 'Unlocks Operations: Purchase Materials',
      title: `${soNum} • ${clientName}`,
      message: `[ACTION REQUIRED]: NEW SO (${soNum}), proceed to plan production.`,
      timestamp: createdTimestamp,
    });

    // 2. Operations Role (Unlocks after Step 1 is done)
    if (isProductionPlanned) {
      generated.push({
        id: `wf_operations_${soNum}`,
        orderId,
        soNumber: soNum,
        role: 'operations',
        roleLabel: 'Operations',
        assignedTo: getAssigneesLabel('operations'),
        actionRequired: !isMaterialsPurchased,
        stepKey: 'materialsPurchased',
        stepCompleted: isMaterialsPurchased,
        nextStepHint: 'Proceed to Pack, Ship & Invoice',
        title: `${soNum} • ${clientName}`,
        message: `[ACTION REQUIRED]: NEW SO (${soNum}), proceed to purchase materials.`,
        timestamp: createdTimestamp,
      });
    }

    // 3. Logistics Role (Unlocks after Packed, Shipped & Invoiced if customs required)
    if (isPackedShippedInvoiced && isCustomsRequired) {
      generated.push({
        id: `wf_logistics_customs_${soNum}`,
        orderId,
        soNumber: soNum,
        role: 'logistics',
        roleLabel: 'Logistics',
        assignedTo: getAssigneesLabel('logistics'),
        actionRequired: !isCustomsPrepared,
        stepKey: 'customsPrepared',
        stepCompleted: isCustomsPrepared,
        nextStepHint: 'Unlocks Accounting: Final Invoice Submission',
        title: `${soNum} • Customs Documents Required`,
        message: `[ACTION REQUIRED]: Please prepare customs documents for ${soNum}.`,
        timestamp: createdTimestamp,
      });
    }

    // 4. Accounting Role (Unlocks after all prior steps are completed)
    const allPriorStepsDone =
      isProductionPlanned &&
      isMaterialsPurchased &&
      isPackedShippedInvoiced &&
      (!isCustomsRequired || isCustomsPrepared);

    if (allPriorStepsDone) {
      generated.push({
        id: `wf_accounting_${soNum}`,
        orderId,
        soNumber: soNum,
        role: 'accounting',
        roleLabel: 'Accounting',
        assignedTo: getAssigneesLabel('accounting'),
        actionRequired: !isAccountingSubmitted,
        stepKey: 'accountingSubmitted',
        stepCompleted: isAccountingSubmitted,
        nextStepHint: 'Completes Sales Order Workflow',
        title: `${targetDisplayLabel} • ${clientName}`,
        message: `[ACTION REQUIRED]: Please submit invoices for ${targetDisplayLabel} to customer and confirm once done.`,
        timestamp: createdTimestamp,
      });
    }
  });

  return generated;
}

export default function NotificationsPanel({
  isOpen = true,
  orders = [],
  manualGroups = {},
  currentUser = null,
  onClose,
  onSelectOrder,
  permissions
}) {
  const [isVisible, setIsVisible] = useState(Boolean(isOpen));
  const [roleAssignments, setRoleAssignments] = useState({
    sales: [],
    production: [],
    operations: [],
    logistics: [],
    shipping_dep: [],
    accounting: [],
  });
  const [dbUsers, setDbUsers] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [savingRoleFor, setSavingRoleFor] = useState(null);
  const [updatingStepId, setUpdatingStepId] = useState(null);

  const [stepStateMap, setStepStateMap] = useState({});
  const [selectedUserByDept, setSelectedUserByDept] = useState({});
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all');
  const [showOnlyActionRequired, setShowOnlyActionRequired] = useState(false);
  const [showRoleManager, setShowRoleManager] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setIsVisible(Boolean(isOpen));
  }, [isOpen]);

  const loggedInUser = useMemo(() => resolveLoggedInUser(currentUser), [currentUser]);

  const fetchRolesAndUsersFromDB = useCallback(async () => {
    setLoadingRoles(true);
    try {
      const token =
        localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken') ||
        '';
      const res = await fetch(`${API_BASE_URL}/weeklyplanner/notifications/roles`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.roles) {
          setRoleAssignments((prev) => ({ ...prev, ...data.roles }));
        }
        if (Array.isArray(data.users)) {
          setDbUsers(data.users);
        }
      }
    } catch (err) {
      console.error('Failed to load department roles from database:', err);
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  useEffect(() => {
    if (isVisible) {
      fetchRolesAndUsersFromDB();
    }
  }, [isVisible, fetchRolesAndUsersFromDB]);

  const userAllowedDepartments = useMemo(() => {
    const matched = ['whole_team'];
    if (!loggedInUser) return matched;

    const uId = String(loggedInUser.userId || '').toLowerCase().trim();
    const uEmail = String(loggedInUser.email || '').toLowerCase().trim();
    const uName = String(loggedInUser.name || '').toLowerCase().trim();

    Object.entries(roleAssignments).forEach(([deptKey, assignedList]) => {
      if (!Array.isArray(assignedList)) return;
      const isMatch = assignedList.some((assigned) => {
        if (!assigned) return false;
        if (typeof assigned === 'string') {
          const val = assigned.toLowerCase().trim();
          return (uName && val === uName) || (uEmail && val === uEmail);
        }
        const aId = String(assigned.userId || assigned._id || '').toLowerCase().trim();
        const aEmail = String(assigned.email || '').toLowerCase().trim();
        const aName = String(assigned.name || '').toLowerCase().trim();

        return (
          (uId && aId && uId === aId) ||
          (uEmail && aEmail && uEmail === aEmail) ||
          (uName && aName && uName === aName)
        );
      });

      if (isMatch && !matched.includes(deptKey)) {
        matched.push(deptKey);
      }
    });

    return matched;
  }, [loggedInUser, roleAssignments]);

  const userDepartmentNotifications = useMemo(() => {
    const allGenerated = buildRoleWorkflowNotifications(
      orders,
      manualGroups,
      stepStateMap,
      roleAssignments
    );

    return allGenerated.filter((notif) => userAllowedDepartments.includes(notif.role));
  }, [orders, manualGroups, stepStateMap, roleAssignments, userAllowedDepartments]);

  const filteredNotifications = useMemo(() => {
    return userDepartmentNotifications.filter((item) => {
      if (selectedRoleFilter !== 'all' && item.role !== selectedRoleFilter) {
        return false;
      }

      if (showOnlyActionRequired && !item.actionRequired) {
        return false;
      }

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = (item.title || '').toLowerCase().includes(q);
        const matchMsg = (item.message || '').toLowerCase().includes(q);
        const matchRole = (item.roleLabel || '').toLowerCase().includes(q);
        if (!matchTitle && !matchMsg && !matchRole) return false;
      }

      return true;
    });
  }, [userDepartmentNotifications, selectedRoleFilter, showOnlyActionRequired, searchQuery]);

  const handleAssignUserToDepartment = async (department) => {
    const selectedUserId = selectedUserByDept[department];
    if (!selectedUserId) return;

    const userObj = dbUsers.find((u) => String(u.userId) === String(selectedUserId));
    if (!userObj) return;

    setSavingRoleFor(department);
    try {
      const token =
        localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken') ||
        '';
      const res = await fetch(`${API_BASE_URL}/weeklyplanner/notifications/roles/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          department,
          user: userObj,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.roles) {
          setRoleAssignments((prev) => ({ ...prev, ...data.roles }));
        }
        setSelectedUserByDept((prev) => ({ ...prev, [department]: '' }));
      }
    } catch (err) {
      console.error('Error assigning user to department:', err);
    } finally {
      setSavingRoleFor(null);
    }
  };

  const handleRemoveUserFromDepartment = async (department, assignedUser) => {
    setSavingRoleFor(department);
    try {
      const token =
        localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken') ||
        '';
      const res = await fetch(`${API_BASE_URL}/weeklyplanner/notifications/roles/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          department,
          userId: assignedUser.userId,
          email: assignedUser.email,
          name: assignedUser.name,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.roles) {
          setRoleAssignments((prev) => ({ ...prev, ...data.roles }));
        }
      }
    } catch (err) {
      console.error('Error removing user from department:', err);
    } finally {
      setSavingRoleFor(null);
    }
  };

  const handleMarkStepDone = async (notif) => {
    if (!notif.stepKey || notif.stepKey === 'broadcast_new_so') return;

    const nextCompletedState = !notif.stepCompleted;
    setUpdatingStepId(notif.id);

    try {
      const token =
        localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken') ||
        '';

      const res = await fetch(`${API_BASE_URL}/weeklyplanner/notifications/order-step`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          orderId: notif.orderId,
          salesOrderNumber: notif.soNumber,
          stepKey: notif.stepKey,
          completed: nextCompletedState,
          updatedBy: loggedInUser?.name || loggedInUser?.email || '',
        }),
      });

      if (res.ok) {
        setStepStateMap((prev) => ({
          ...prev,
          [notif.orderId]: {
            ...(prev[notif.orderId] || {}),
            [notif.stepKey]: nextCompletedState,
          },
          [notif.soNumber]: {
            ...(prev[notif.soNumber] || {}),
            [notif.stepKey]: nextCompletedState,
          },
        }));
      }
    } catch (err) {
      console.error('Failed to update workflow step in analyticssalesdashboards:', err);
    } finally {
      setUpdatingStepId(null);
    }
  };

  const handleCloseWindow = () => {
    setIsVisible(false);
    if (onClose) onClose();
  };

  if (!isVisible) return null;

  const actionRequiredCount = filteredNotifications.filter((n) => n.actionRequired).length;
  const visibleRoleTabs = NOTIFICATION_ROLES.filter((r) =>
    userAllowedDepartments.includes(r.id)
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30 backdrop-blur-[1px]">
      <div className="flex-1" onClick={handleCloseWindow} />

      <div className="w-full max-w-xl bg-white shadow-2xl border-l border-slate-200 flex flex-col h-full">
        {/* Header */}
        <div className="px-4 py-3 bg-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-blue-600/20 text-blue-400">
              <Bell className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold tracking-wide">Department Notifications</h2>
                {actionRequiredCount > 0 && (
                  <span className="px-2 py-0.5 text-[10px] font-bold bg-rose-600 text-white rounded-full">
                    {actionRequiredCount} Action Required
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400">
                {loggedInUser
                  ? `Logged in as: ${loggedInUser.name || loggedInUser.email}`
                  : 'Showing notifications for your assigned department roles'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {permissions && (
              <button
                type="button"
                onClick={() => setShowRoleManager((v) => !v)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                  showRoleManager
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                }`}
                title="Manage Department Role Assignments"
              >
                <UserCheck className="w-3.5 h-3.5" />
                <span>Manage Roles</span>
              </button>
            )}

            <button
              type="button"
              onClick={handleCloseWindow}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              title="Close Notifications"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* User Department Roles Strip */}
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold text-slate-600">Your Departments:</span>
            {visibleRoleTabs.map((r) => (
              <span
                key={r.id}
                className={`px-2 py-0.5 text-[10px] font-bold rounded border ${r.color}`}
              >
                {r.label}
              </span>
            ))}
          </div>

          { permissions && (
            <button
              type="button"
              onClick={() => setShowOnlyActionRequired((v) => !v)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors flex items-center gap-1 ${
                showOnlyActionRequired
                  ? 'bg-rose-50 text-rose-700 border-rose-300'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'
              }`}
            >
              <AlertTriangle className="w-3 h-3" />
              <span>Action Required Only</span>
            </button>
            )
          }
        </div>

        {/* Database-Driven Manage Roles Drawer */}
        {showRoleManager && (
          <div className="p-4 bg-blue-50/60 border-b border-blue-200 space-y-3 max-h-100 overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Department Role Assignments
                </h3>
                <p className="text-[11px] text-slate-600">
                  Assign database users to each department. Users will only see notifications for their assigned departments.
                </p>
              </div>
              {loadingRoles && <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />}
            </div>

            <div className="grid grid-cols-1 gap-2">
              {NOTIFICATION_ROLES.filter((r) => r.id !== 'whole_team').map((role) => {
                const assignedList = roleAssignments[role.id] || [];
                const isSavingThisRole = savingRoleFor === role.id;

                return (
                  <div
                    key={role.id}
                    className="p-2.5 bg-white rounded-lg border border-slate-200 flex flex-col gap-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`px-2 py-0.5 text-[11px] font-bold rounded border ${role.color}`}
                      >
                        {role.label}
                      </span>

                      <div className="flex flex-wrap items-center gap-1">
                        {assignedList.length === 0 ? (
                          <span className="text-[11px] text-rose-600 font-medium">Unassigned</span>
                        ) : (
                          assignedList.map((u) => {
                            const key = u.userId || u.email || u.name;
                            const label = u.name
                              ? `${u.name}${u.email ? ` (${u.email})` : ''}`
                              : u.email;
                            return (
                              <span
                                key={key}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-800 border border-slate-300"
                              >
                                {label}
                                <button
                                  type="button"
                                  disabled={isSavingThisRole}
                                  onClick={() => handleRemoveUserFromDepartment(role.id, u)}
                                  className="text-slate-400 hover:text-rose-600 disabled:opacity-50"
                                  title={`Remove ${u.name || u.email} from ${role.label}`}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            );
                          })
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <select
                        value={selectedUserByDept[role.id] || ''}
                        onChange={(e) =>
                          setSelectedUserByDept((prev) => ({
                            ...prev,
                            [role.id]: e.target.value,
                          }))
                        }
                        className="flex-1 text-xs bg-white border border-slate-300 rounded px-2 py-1.5 text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">Select database user to assign to {role.label}...</option>
                        {dbUsers.map((dbUser) => (
                          <option key={dbUser.userId} value={dbUser.userId}>
                            {dbUser.name} {dbUser.email ? `(${dbUser.email})` : ''}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        disabled={!selectedUserByDept[role.id] || isSavingThisRole}
                        onClick={() => handleAssignUserToDepartment(role.id)}
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        {isSavingThisRole ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <span>Assign</span>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!showRoleManager && (
          <>
            {/* Department Filter Tabs (Filtered strictly to logged-in user's departments) */}
            <div className="px-4 py-2 bg-white border-b border-slate-200 flex items-center gap-1.5 overflow-x-auto">
              {
              /*
              <button
                type="button"
                onClick={() => setSelectedRoleFilter('all')}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors ${
                  selectedRoleFilter === 'all'
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                My Assigned Departments ({userDepartmentNotifications.length})
              </button>
              */
              }

              {visibleRoleTabs.map((role) => {
                const count = userDepartmentNotifications.filter((n) => n.role === role.id).length;
                return (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => setSelectedRoleFilter(role.id)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors ${
                      selectedRoleFilter === role.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {role.label} ({count})
                  </button>
                );
              })}
            </div>

            {/* Search Bar */}
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by SO#, PO Family, Client, or Department..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5 bg-slate-50/50">

              {filteredNotifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs font-semibold text-slate-600">
                    No notifications found for your assigned departments.
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {userAllowedDepartments.length <= 1
                      ? 'You are not currently assigned to a specific department role. Please contact your administrator to be assigned to a department.'
                      : 'All workflow tasks for your departments are up to date.'}
                  </p>
                </div>
              ) : (
                filteredNotifications.map((notif) => {
                  const roleMeta =
                    NOTIFICATION_ROLES.find((r) => r.id === notif.role) || NOTIFICATION_ROLES[0];
                  const isUpdatingThisStep = updatingStepId === notif.id;

                  return (
                    <div
                      key={notif.id}
                      className={`p-3 rounded-xl border transition-all ${
                        notif.actionRequired
                          ? 'bg-white border-rose-300 shadow-sm ring-1 ring-rose-100'
                          : 'bg-white border-slate-200 shadow-sm'
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span
                            className={`px-2 py-0.5 text-[10px] font-bold rounded border ${roleMeta.color}`}
                          >
                            Dept: {roleMeta.label}
                          </span>

                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-slate-100 text-slate-700 border border-slate-200">
                            Assigned: {notif.assignedTo}
                          </span>

                          {notif.actionRequired && (
                            <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-rose-600 text-white">
                              ACTION REQUIRED
                            </span>
                          )}

                          {notif.stepCompleted && notif.stepKey !== 'broadcast_new_so' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                              <CheckCircle2 className="w-3 h-3" />
                              Step Completed
                            </span>
                          )}
                        </div>

                        <span className="text-[10px] font-semibold text-slate-400">
                          {notif.soNumber}
                        </span>
                      </div>

                      <div className="text-xs font-bold text-slate-800 mb-0.5">{notif.title}</div>
                      <p
                        className={`text-xs leading-relaxed ${
                          notif.actionRequired ? 'font-semibold text-rose-900' : 'text-slate-700'
                        }`}
                      >
                        {notif.message}
                      </p>

                      {notif.stepKey && notif.stepKey !== 'broadcast_new_so' && (
                        <div className="mt-2.5 pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
                          <div className="text-[10px] text-slate-500">
                            {notif.nextStepHint && <span>Next: {notif.nextStepHint}</span>}
                          </div>

                          <div className="flex items-center gap-1.5">
                            { permissions && (
                              <button
                                type="button"
                                disabled={isUpdatingThisStep}
                                onClick={() => handleMarkStepDone(notif)}
                                className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold flex items-center gap-1 transition-colors disabled:opacity-50 ${
                                  notif.stepCompleted
                                    ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300'
                                    : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm'
                                }`}
                              >
                                {isUpdatingThisStep ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Check className="w-3.5 h-3.5" />
                                )}
                                <span>
                                  {notif.stepCompleted
                                    ? 'Completed (Click to Undo)'
                                    : notif.role === 'accounting'
                                    ? 'Confirm Invoice Submitted'
                                    : 'Mark Done'}
                                </span>
                              </button>
                              )
                            }

                            {onSelectOrder && notif.orderId && (
                              <button
                                type="button"
                                onClick={() => onSelectOrder(notif.orderId)}
                                className="px-2 py-1 rounded-lg text-[11px] font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
                              >
                                View SO
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 bg-white border-t border-slate-200 flex items-center justify-between">
              <span className="text-[11px] text-slate-500">
                Showing {filteredNotifications.length} notification(s) for your assigned departments
              </span>
              <button
                type="button"
                onClick={handleCloseWindow}
                className="px-3 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                Close
              </button>
            </div>
          </>
        )}


      </div>
    </div>
  );
}